import type { EntraAttributesSnapshot } from '../types';

export const ENTRA_GRAPH_ME_SELECT_FIELDS = [
    'id',
    'displayName',
    'givenName',
    'surname',
    'mail',
    'userPrincipalName',
    'country',
    'usageLocation',
    'state',
    'city',
    'officeLocation',
    'companyName',
    'department',
    'jobTitle',
    'employeeId',
    'employeeType',
    'employeeOrgData',
    'onPremisesExtensionAttributes',
].join(',');

export const ENTRA_GRAPH_ME_SELECT_URL = `https://graph.microsoft.com/v1.0/me?$select=${ENTRA_GRAPH_ME_SELECT_FIELDS}`;
export const ENTRA_GRAPH_ME_FALLBACK_URL = 'https://graph.microsoft.com/v1.0/me';

export type CorporateRegionCode = 'NA' | 'LAC' | 'EMEA' | 'APJ';

/**
 * ISO 3166-1 alpha-2 country code to TD SYNNEX corporate region mapping.
 * Used when Entra ID does not provide an explicit region extension attribute.
 */
const ISO_COUNTRY_TO_REGION: Record<string, CorporateRegionCode> = {
    // North America (NA)
    US: 'NA',
    CA: 'NA',

    // Latin America & Caribbean (LAC)
    MX: 'LAC',
    BR: 'LAC',
    AR: 'LAC',
    CL: 'LAC',
    CO: 'LAC',
    PE: 'LAC',
    EC: 'LAC',
    UY: 'LAC',
    PY: 'LAC',
    BO: 'LAC',
    VE: 'LAC',
    CR: 'LAC',
    PA: 'LAC',
    GT: 'LAC',
    DO: 'LAC',
    PR: 'LAC',
    HN: 'LAC',
    SV: 'LAC',
    NI: 'LAC',
    JM: 'LAC',
    TT: 'LAC',

    // Europe, Middle East & Africa (EMEA)
    ES: 'EMEA',
    DE: 'EMEA',
    FR: 'EMEA',
    GB: 'EMEA',
    UK: 'EMEA',
    IT: 'EMEA',
    NL: 'EMEA',
    BE: 'EMEA',
    LU: 'EMEA',
    CH: 'EMEA',
    AT: 'EMEA',
    PT: 'EMEA',
    IE: 'EMEA',
    SE: 'EMEA',
    NO: 'EMEA',
    DK: 'EMEA',
    FI: 'EMEA',
    PL: 'EMEA',
    CZ: 'EMEA',
    SK: 'EMEA',
    HU: 'EMEA',
    RO: 'EMEA',
    BG: 'EMEA',
    GR: 'EMEA',
    HR: 'EMEA',
    SI: 'EMEA',
    EE: 'EMEA',
    LV: 'EMEA',
    LT: 'EMEA',
    TR: 'EMEA',
    IL: 'EMEA',
    AE: 'EMEA',
    SA: 'EMEA',
    QA: 'EMEA',
    KW: 'EMEA',
    ZA: 'EMEA',
    EG: 'EMEA',
    MA: 'EMEA',
    NG: 'EMEA',
    KE: 'EMEA',

    // Asia-Pacific & Japan (APJ)
    JP: 'APJ',
    AU: 'APJ',
    NZ: 'APJ',
    SG: 'APJ',
    IN: 'APJ',
    CN: 'APJ',
    HK: 'APJ',
    TW: 'APJ',
    KR: 'APJ',
    MY: 'APJ',
    TH: 'APJ',
    ID: 'APJ',
    PH: 'APJ',
    VN: 'APJ',
};

const COUNTRY_NAME_TO_ISO: Record<string, string> = {
    'united states': 'US',
    'united states of america': 'US',
    usa: 'US',
    canada: 'CA',
    mexico: 'MX',
    brazil: 'BR',
    argentina: 'AR',
    chile: 'CL',
    colombia: 'CO',
    peru: 'PE',
    costa_rica: 'CR',
    'costa rica': 'CR',
    spain: 'ES',
    españa: 'ES',
    germany: 'DE',
    deutschland: 'DE',
    france: 'FR',
    'united kingdom': 'GB',
    'great britain': 'GB',
    england: 'GB',
    italy: 'IT',
    italia: 'IT',
    netherlands: 'NL',
    'the netherlands': 'NL',
    belgium: 'BE',
    switzerland: 'CH',
    austria: 'AT',
    portugal: 'PT',
    ireland: 'IE',
    sweden: 'SE',
    norway: 'NO',
    denmark: 'DK',
    finland: 'FI',
    poland: 'PL',
    'czech republic': 'CZ',
    czechia: 'CZ',
    slovakia: 'SK',
    hungary: 'HU',
    romania: 'RO',
    turkey: 'TR',
    israel: 'IL',
    'united arab emirates': 'AE',
    'saudi arabia': 'SA',
    'south africa': 'ZA',
    japan: 'JP',
    australia: 'AU',
    'new zealand': 'NZ',
    singapore: 'SG',
    india: 'IN',
    china: 'CN',
    'hong kong': 'HK',
    taiwan: 'TW',
    'south korea': 'KR',
    korea: 'KR',
    malaysia: 'MY',
    thailand: 'TH',
    indonesia: 'ID',
    philippines: 'PH',
    vietnam: 'VN',
};

const EXPLICIT_REGION_ALIASES: Record<string, CorporateRegionCode> = {
    NA: 'NA',
    NAM: 'NA',
    AMER: 'NA',
    AMERICAS: 'NA',
    'NORTH AMERICA': 'NA',
    LAC: 'LAC',
    LATAM: 'LAC',
    'LATIN AMERICA': 'LAC',
    EMEA: 'EMEA',
    EU: 'EMEA',
    EUROPE: 'EMEA',
    APJ: 'APJ',
    APAC: 'APJ',
    'ASIA PACIFIC': 'APJ',
    ASIA: 'APJ',
};

function cleanString(value: unknown): string | undefined {
    if (typeof value !== 'string') return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Resolves a 2-letter ISO code into a full English country name using `Intl.DisplayNames`.
 */
export function resolveCountryNameFromIso(isoCode: string): string {
    const normalized = isoCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(normalized)) {
        return isoCode.trim();
    }
    try {
        const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
        return displayNames.of(normalized) || normalized;
    } catch {
        return normalized;
    }
}

/**
 * Normalizes an explicit region value (e.g. from an Entra extensionAttribute) if it matches
 * a known corporate region token.
 */
export function matchExplicitRegionToken(raw: unknown): CorporateRegionCode | undefined {
    const cleaned = cleanString(raw);
    if (!cleaned) return undefined;
    return EXPLICIT_REGION_ALIASES[cleaned.toUpperCase()];
}

/**
 * Extracts a corporate region code from composite AD/Entra partition labels
 * such as `extensionAttribute12: "EU-Cloud"`, `"NA-Cloud"`, `"APJ-Cloud"`, `"LAC-Cloud"`.
 */
export function matchRegionPrefixToken(raw: unknown): CorporateRegionCode | undefined {
    const cleaned = cleanString(raw);
    if (!cleaned) return undefined;
    const prefix = cleaned.split(/[-_\s]/)[0]?.toUpperCase();
    if (!prefix) return undefined;
    return EXPLICIT_REGION_ALIASES[prefix];
}

/**
 * Derives the TD SYNNEX corporate region (`NA`, `LAC`, `EMEA`, `APJ`) from an ISO country code
 * or country name.
 */
export function deriveRegionFromCountry(
    countryCode?: string,
    countryName?: string,
): CorporateRegionCode | undefined {
    if (countryCode) {
        const iso = countryCode.trim().toUpperCase();
        if (ISO_COUNTRY_TO_REGION[iso]) {
            return ISO_COUNTRY_TO_REGION[iso];
        }
    }
    if (countryName) {
        const key = countryName.trim().toLowerCase();
        const iso = COUNTRY_NAME_TO_ISO[key];
        if (iso && ISO_COUNTRY_TO_REGION[iso]) {
            return ISO_COUNTRY_TO_REGION[iso];
        }
    }
    return undefined;
}

export interface ExtractedEntraOrgAttributes {
    country?: string;
    countryCode?: string;
    region?: string;
    legalEntity?: string;
    entraAttributesSnapshot?: EntraAttributesSnapshot;
}

/**
 * Extracts normalized `country`, `countryCode`, `region`, `legalEntity`, and a diagnostic
 * `entraAttributesSnapshot` from a Microsoft Graph `/v1.0/me` response and/or OIDC token claims.
 */
export function extractEntraOrganizationAttributes(
    graphProfile?: Record<string, unknown> | null,
    tokenClaims?: Record<string, unknown> | null,
): ExtractedEntraOrgAttributes {
    const graph = graphProfile ?? {};
    const claims = tokenClaims ?? {};

    const rawCountry =
        cleanString(graph.country) ||
        cleanString(claims.ctry) ||
        cleanString(claims.country);
    const rawUsageLocation = cleanString(graph.usageLocation);

    // 1. Resolve 2-letter ISO countryCode
    let countryCode: string | undefined;
    if (rawUsageLocation && /^[A-Za-z]{2}$/.test(rawUsageLocation)) {
        countryCode = rawUsageLocation.toUpperCase();
    } else if (rawCountry && /^[A-Za-z]{2}$/.test(rawCountry)) {
        countryCode = rawCountry.toUpperCase();
    } else if (rawCountry) {
        const mappedIso = COUNTRY_NAME_TO_ISO[rawCountry.toLowerCase()];
        if (mappedIso) {
            countryCode = mappedIso;
        }
    }

    // 2. Resolve human-readable country name
    let country: string | undefined;
    if (rawCountry && rawCountry.length > 2) {
        country = rawCountry;
    } else if (countryCode) {
        country = resolveCountryNameFromIso(countryCode);
    } else if (rawCountry) {
        country = rawCountry;
    }

    // 3. Inspect onPremisesExtensionAttributes (extensionAttribute1..15)
    const rawExt =
        graph.onPremisesExtensionAttributes &&
        typeof graph.onPremisesExtensionAttributes === 'object'
            ? (graph.onPremisesExtensionAttributes as Record<string, unknown>)
            : {};
    const nonEmptyExtensionAttributes: Record<string, string> = {};
    let extensionRegion: CorporateRegionCode | undefined;

    for (const [key, val] of Object.entries(rawExt)) {
        const cleaned = cleanString(val);
        if (cleaned) {
            nonEmptyExtensionAttributes[key] = cleaned;
            if (!extensionRegion) {
                extensionRegion = matchExplicitRegionToken(cleaned);
            }
        }
    }

    // 4. Resolve Region (prefer explicit Entra extension attribute or claim, then ISO country lookup,
    // then TD SYNNEX AD tenant partition prefix in extensionAttribute12 such as "EU-Cloud")
    const claimRegion =
        matchExplicitRegionToken(claims.region) ||
        matchExplicitRegionToken(graph.region);
    const region =
        extensionRegion ||
        claimRegion ||
        deriveRegionFromCountry(countryCode, country) ||
        matchRegionPrefixToken(nonEmptyExtensionAttributes.extensionAttribute12);

    // 5. Resolve Legal Entity (in corporate Entra ID, `companyName` on the user object holds the
    // employing subsidiary / legal entity synced from HR, e.g., "TD SYNNEX Spain S.L.U.")
    const rawEmployeeOrgData =
        graph.employeeOrgData && typeof graph.employeeOrgData === 'object'
            ? (graph.employeeOrgData as Record<string, unknown>)
            : undefined;
    const division = cleanString(rawEmployeeOrgData?.division);
    const costCenter = cleanString(rawEmployeeOrgData?.costCenter);

    const legalEntity =
        cleanString(graph.companyName) ||
        cleanString(claims.companyName) ||
        division;

    // 6. Build diagnostic snapshot so admins/developers can inspect exact Entra fields in Firestore
    const snapshot: EntraAttributesSnapshot = {
        ...(cleanString(graph.country) ? { country: cleanString(graph.country) } : {}),
        ...(rawUsageLocation ? { usageLocation: rawUsageLocation } : {}),
        ...(cleanString(graph.state) ? { state: cleanString(graph.state) } : {}),
        ...(cleanString(graph.city) ? { city: cleanString(graph.city) } : {}),
        ...(cleanString(graph.officeLocation)
            ? { officeLocation: cleanString(graph.officeLocation) }
            : {}),
        ...(cleanString(graph.companyName) ? { companyName: cleanString(graph.companyName) } : {}),
        ...(cleanString(graph.department) ? { department: cleanString(graph.department) } : {}),
        ...(cleanString(graph.jobTitle) ? { jobTitle: cleanString(graph.jobTitle) } : {}),
        ...(cleanString(graph.employeeType)
            ? { employeeType: cleanString(graph.employeeType) }
            : {}),
        ...(division || costCenter
            ? {
                  employeeOrgData: {
                      ...(division ? { division } : {}),
                      ...(costCenter ? { costCenter } : {}),
                  },
              }
            : {}),
        ...(Object.keys(nonEmptyExtensionAttributes).length > 0
            ? { extensionAttributes: nonEmptyExtensionAttributes }
            : {}),
        capturedAt: new Date().toISOString(),
    };

    const hasSnapshotData = Object.keys(snapshot).length > 1; // more than just capturedAt

    return {
        ...(country ? { country } : {}),
        ...(countryCode ? { countryCode } : {}),
        ...(region ? { region } : {}),
        ...(legalEntity ? { legalEntity } : {}),
        ...(hasSnapshotData ? { entraAttributesSnapshot: snapshot } : {}),
    };
}

/**
 * Fetches the signed-in user's directory profile from Microsoft Graph `/v1.0/me`
 * using their delegated OAuth `accessToken`.
 */
export async function fetchEntraGraphMeProfile(
    accessToken: string,
    timeoutMs = 3500,
): Promise<Record<string, unknown> | null> {
    if (!accessToken) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        let response = await fetch(ENTRA_GRAPH_ME_SELECT_URL, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${accessToken}`,
                Accept: 'application/json',
            },
            signal: controller.signal,
        });

        // If a tenant schema rejects one of the extended $select fields, retry base /v1.0/me
        if (response.status === 400) {
            response = await fetch(ENTRA_GRAPH_ME_FALLBACK_URL, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    Accept: 'application/json',
                },
                signal: controller.signal,
            });
        }

        if (!response.ok) {
            return null;
        }

        const data = (await response.json()) as Record<string, unknown>;
        return data;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}
